import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';
import { T } from '../theme';

const POPOVER_WIDTH = 230;
const OFFSET = 8;

export function HelpTip({ content, placement = 'top' }) {
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!rect) return;
    const handler = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target)) setRect(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [rect]);

  const handleClick = (e) => {
    e.stopPropagation();
    setRect(r => r ? null : btnRef.current.getBoundingClientRect());
  };

  const above = placement === 'top';

  // Horizontal: center on the button, clamped inside viewport
  const rawLeft = rect ? rect.left + rect.width / 2 - POPOVER_WIDTH / 2 : 0;
  const clampedLeft = Math.max(8, Math.min(rawLeft, window.innerWidth - POPOVER_WIDTH - 8));
  // Arrow offset relative to the popover box so it still points at the button
  const arrowLeft = rect ? Math.max(10, Math.min(rect.left + rect.width / 2 - clampedLeft, POPOVER_WIDTH - 10)) : POPOVER_WIDTH / 2;

  const fixedStyle = rect ? {
    position: 'fixed',
    left: clampedLeft,
    ...(above
      ? { bottom: window.innerHeight - rect.top + OFFSET }
      : { top: rect.bottom + OFFSET }),
    zIndex: 9999,
    width: POPOVER_WIDTH,
    background: T.navy,
    color: '#fff',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.5,
    padding: '9px 12px',
    borderRadius: 9,
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    whiteSpace: 'pre-wrap',
  } : {};

  return (
    <span ref={btnRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        onClick={handleClick}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', color: '#9CA3AF' }}
        aria-label="Help"
      >
        <HelpCircle size={14} />
      </button>
      {rect && createPortal(
        <div style={fixedStyle}>
          <div style={{
            position: 'absolute',
            [above ? 'bottom' : 'top']: -5,
            left: arrowLeft,
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            [above ? 'borderTop' : 'borderBottom']: `5px solid ${T.navy}`,
          }} />
          {content}
        </div>,
        document.body
      )}
    </span>
  );
}
